import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

interface ErrorModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  details?: string;
  onClose: () => void;
  onRetry?: () => void;
}

export default function ErrorModal({ isOpen, title = 'Ocurrió un error', message, details, onClose, onRetry }: ErrorModalProps) {
  const [showDetails, setShowDetails] = useState(false);
  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4" style={{ zIndex: 2147483647 }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md border border-red-100 overflow-hidden">
        <div className="bg-gradient-to-r from-red-50 to-rose-50 p-6 border-b border-red-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center text-red-600 text-2xl">⚠️</div>
            <div>
              <h3 className="text-lg font-bold text-[#2C4358]">{title}</h3>
              <p className="text-sm text-red-600/80">Por favor, revisa la información y vuelve a intentarlo.</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="text-[#2C4358] text-sm leading-relaxed mb-3">
            {(() => {
              const safeMessage = typeof message === 'string' ? message : (message ? JSON.stringify(message) : '');
              return safeMessage;
            })()}
            {(() => {
              const safeMessage = typeof message === 'string' ? message : (message ? JSON.stringify(message) : '');
              return typeof safeMessage === 'string' && safeMessage.includes('Actualiza tu plan') ? (
              <div className="mt-3">
                <Link
                  to="/pricing"
                  onClick={onClose}
                  className="inline-flex items-center text-sm font-semibold text-[#22B6B2] hover:text-[#1a8f8c] underline"
                >
                  Ver planes y actualizar →
                </Link>
                </div>
              ) : null;
            })()}
          </div>
          {details && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="text-xs text-[#22B6B2] hover:text-[#1a8f8c] font-semibold"
              >
                {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
              </button>
              {showDetails && (
                <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded-xl p-3 overflow-auto max-h-40 text-gray-700 whitespace-pre-wrap">{details}</pre>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border-2 border-gray-200 text-[#2C4358] rounded-xl font-semibold hover:bg-gray-100"
          >
            Cerrar
          </button>
          {onRetry && (
            <button
              type="button"
              onClick={() => { onClose(); onRetry(); }}
              className="px-4 py-2 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white rounded-xl font-semibold hover:opacity-95"
            >
              Reintentar
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
